import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import BackButton from './BackButton.js';
import './index.css';

// /scan — door tool. A ticket QR encodes /scan?t=<token>, so any phone
// camera opens this page pointed at a ticket. It shows the ticket's status
// (valid / used / invalid); admitting the holder ("Admit") burns the ticket
// and needs the shared staff code, which is kept on the device so it's only
// entered once.
const CODE_KEY = 'mp3j_scan_code';

function readStoredCode() {
  try { return localStorage.getItem(CODE_KEY) || ''; } catch { return ''; }
}

function ScanPage() {
  const [search, setSearch] = useSearchParams();
  const tokenFromUrl = search.get('t') || '';

  const [token, setToken] = useState(tokenFromUrl);
  const [code, setCode] = useState(readStoredCode);
  const [lookup, setLookup] = useState(null);
  const [result, setResult] = useState(null);
  const [checking, setChecking] = useState(false);
  const [redeeming, setRedeeming] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { setToken(tokenFromUrl); }, [tokenFromUrl]);

  const check = useCallback(async (override) => {
    const target = (override ?? token).trim();
    if (!target) return;
    setChecking(true);
    setError('');
    setResult(null);
    setLookup(null);
    try {
      const res = await fetch(`/scan/lookup?t=${encodeURIComponent(target)}`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setError(data.msg || 'Could not check that ticket.'); return; }
      setLookup(data);
    } catch {
      setError('Could not reach the server.');
    } finally {
      setChecking(false);
    }
  }, [token]);

  // auto-check when arriving from a scanned QR link
  useEffect(() => {
    if (tokenFromUrl) check(tokenFromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenFromUrl]);

  function persistCode(value) {
    setCode(value);
    try {
      if (value) localStorage.setItem(CODE_KEY, value);
      else localStorage.removeItem(CODE_KEY);
    } catch { /* private mode / storage blocked — fall back to in-memory only */ }
  }

  const redeem = useCallback(async () => {
    const target = token.trim();
    if (!target) return;
    if (!code.trim()) { setError('Enter the staff code first.'); return; }
    setRedeeming(true);
    setError('');
    try {
      const res = await fetch('/scan/redeem', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: target, code: code.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 403) { setError('Wrong staff code.'); return; }
      if (res.status === 429) { setError(data.msg || 'Too many attempts — wait a bit.'); return; }
      if (!res.ok && res.status !== 409 && res.status !== 404) {
        setError(data.msg || 'Could not admit that ticket.');
        return;
      }
      setResult(data);
      setLookup(null);
    } catch {
      setError('Could not reach the server.');
    } finally {
      setRedeeming(false);
    }
  }, [token, code]);

  function scanAnother() {
    setToken('');
    setLookup(null);
    setResult(null);
    setError('');
    if (search.get('t')) {
      const next = new URLSearchParams(search);
      next.delete('t');
      setSearch(next, { replace: true });
    }
  }

  const shown = result || lookup;
  const state = shown?.status; // valid | admitted | used | invalid
  const when = shown?.redeemedAt ? new Date(shown.redeemedAt).toLocaleString() : null;

  return (
    <main className='page'>
      <div className='page-content'>
        <BackButton fallback='/' />

        <div className='card'>
          <h1>Scan a ticket</h1>

          <div className='field'>
            <label className='label' htmlFor='scan-code'>Staff code</label>
            <input
              id='scan-code'
              className='input'
              type='password'
              autoComplete='off'
              value={code}
              onChange={(e) => persistCode(e.target.value)}
              placeholder='required to admit'
            />
            <p className='help-text'>Saved on this device — you only enter it once.</p>
          </div>

          <div className='field'>
            <label className='label' htmlFor='scan-token'>Ticket code</label>
            <input
              id='scan-token'
              className='input'
              type='text'
              autoComplete='off'
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder='scan a ticket QR, or paste its code'
            />
          </div>

          <div className='btn-row'>
            <button
              type='button'
              className='btn btn-secondary'
              onClick={() => check()}
              disabled={checking || !token.trim()}
            >
              {checking ? 'Checking…' : 'Check'}
            </button>
            <button
              type='button'
              className='btn btn-primary'
              onClick={redeem}
              disabled={redeeming || !token.trim()}
            >
              {redeeming ? 'Admitting…' : 'Admit'}
            </button>
          </div>

          {error && <p className='error-text'>{error}</p>}
        </div>

        {shown && (
          <div className={`card scan-result scan-${state}`}>
            {state === 'valid' && (
              <>
                <h2>✓ Valid ticket</h2>
                <p>{shown.artistName} — not used yet. Tap “Admit” to let them in.</p>
              </>
            )}
            {state === 'admitted' && (
              <>
                <h2>✓ Admitted</h2>
                <p>{shown.artistName} — marked used just now.</p>
              </>
            )}
            {state === 'used' && (
              <>
                <h2>✕ Already used</h2>
                <p>{shown.artistName} — scanned {when || 'earlier'}.</p>
              </>
            )}
            {state === 'invalid' && (
              <>
                <h2>✕ Not a valid ticket</h2>
                <p>No ticket matches that code.</p>
              </>
            )}
            {state !== 'invalid' && shown.entryCode && (
              <p className='entry-code'>{shown.entryCode}<span> entry code</span></p>
            )}
            {shown.perk === 'free-shot' && state !== 'invalid' && (
              <p className='perk-badge'>🥃 21+ FREE SHOT — check ID</p>
            )}
            <button type='button' className='btn btn-secondary' onClick={scanAnother}>
              Scan another
            </button>
          </div>
        )}
      </div>
    </main>
  );
}

export default ScanPage;
